import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, GrowingTextarea } from './TodoPeople';
import { AiBadge } from './VoiceTodo';
import { durationLabel, longDay } from '../../lib/quickAdd';
import { Icon } from './icons';
import './TodoAssist.css';

/*
 * AI HELP ON ONE TO-DO (to-do Phase 2, batch C) - only when
 * lib/todoAssistGate.js says AI is allowed.
 *
 *   BREAK INTO STEPS    Claude proposes 3 to 10 sub-tasks (with a day, never
 *                       after the to-do's own, and how long each takes when it
 *                       can tell). They come back as a list to tick, reword,
 *                       re-date and reorder; "Add N sub-tasks" makes the
 *                       ticked ones under the to-do through POST /todos, in
 *                       that order.
 *   CHECKLIST FROM TEXT paste an email or a document; Claude lists its action
 *                       items; they are added as checklist lines or as
 *                       sub-tasks - the person's choice.
 *
 * Both are proposals, marked "AI", and nothing is made until the button at
 * the bottom is pressed. What is sent is said on the screen: for steps, the
 * to-do's title, notes, checklist, list and client; for a checklist, the
 * pasted text.
 *
 * `api` is TodoApp's (paths under /todos, with View as's `as=`); `onAdd`
 * does the making, so it goes through the page's own busy and reload.
 */

export const TEXT_MAX = 20000;
const DURATIONS = [5, 10, 15, 30, 45, 60, 90, 120, 180, 240, 480];

/** One proposed line: ticked, its words, and (for steps) its day and length. */
function ProposalList({ rows, setRows, withPlan = false, plan = false, label }) {
  const move = (i, by) => setRows((prev) => {
    const j = i + by;
    if (j < 0 || j >= prev.length) return prev;
    const next = prev.slice();
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const set = (i, patch) => setRows((prev) => prev.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  return (
    <ol className="tva-props" aria-label={label}>
      {rows.map((r, i) => (
        <li key={r.key} className={`tva-prop${r.on ? '' : ' is-off'}`} data-proposal={i}>
          <input type="checkbox" checked={r.on} aria-label={`Keep "${r.title}"`}
                 onChange={(e) => set(i, { on: e.target.checked })} />
          <div className="tva-prop-main">
            <GrowingTextarea rows={1} className="tva-prop-title" value={r.title} maxLength={withPlan ? 300 : 500}
                             aria-label={withPlan ? `Step ${i + 1}` : `Action ${i + 1}`}
                             onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                             onChange={(e) => set(i, { title: e.target.value.replace(/\s*\n+\s*/g, ' ') })} />
            {withPlan && (
              <span className="tva-prop-meta">
                <label className="tva-prop-day">
                  <Icon name="calendar" size={12} />
                  <input type="date" value={r.due_date || ''} aria-label={`When, step ${i + 1}`}
                         onChange={(e) => set(i, { due_date: e.target.value || null })} />
                </label>
                {plan && (
                  <label className="tva-prop-len">
                    <Icon name="duration" size={12} />
                    <select value={r.duration_minutes || ''} aria-label={`How long, step ${i + 1}`}
                            onChange={(e) => set(i, { duration_minutes: Number(e.target.value) || null })}>
                      <option value="">-</option>
                      {[...new Set([...DURATIONS, ...(r.duration_minutes ? [r.duration_minutes] : [])])].sort((a, b) => a - b)
                        .map((m) => <option key={m} value={m}>{durationLabel(m)}</option>)}
                    </select>
                  </label>
                )}
              </span>
            )}
            {!withPlan && r.notes && <span className="tva-prop-notes">{r.notes}</span>}
          </div>
          <span className="tva-prop-order">
            <button type="button" className="tds-icon-btn" disabled={i === 0} aria-label={`Move step ${i + 1} up`}
                    onClick={() => move(i, -1)}><Icon name="arrowUp" size={14} /></button>
            <button type="button" className="tds-icon-btn" disabled={i === rows.length - 1} aria-label={`Move step ${i + 1} down`}
                    onClick={() => move(i, 1)}><Icon name="arrowDown" size={14} /></button>
          </span>
        </li>
      ))}
    </ol>
  );
}

/** The skeleton while Claude thinks: three soft rows, and words. */
const Thinking = ({ words }) => (
  <div className="tva-thinking" role="status" aria-live="polite">
    <span className="tva-spin" aria-hidden="true" />
    <span>{words}</span>
    <span className="tva-skel" aria-hidden="true"><i /><i /><i /></span>
  </div>
);

let keySeq = 0;
const keyed = (rows) => rows.map((r) => ({ ...r, on: true, key: `p${++keySeq}` }));

/**
 * BREAK INTO STEPS. Asks as soon as it opens; Try again asks again.
 * `onAdd(steps)` gets the ticked rows, in order, with their titles trimmed.
 */
export function StepsDialog({ item, api, plan = false, onAdd, onClose }) {
  const [rows, setRows] = useState(null);
  const [problem, setProblem] = useState(null);
  const [busy, setBusy] = useState(false);
  const live = useRef(true);
  useEffect(() => () => { live.current = false; }, []);

  const ask = useCallback(() => {
    setRows(null);
    setProblem(null);
    api.post(`/${item.id}/assist/steps`, {})
      .then((r) => { if (live.current) setRows(keyed(Array.isArray(r.data?.steps) ? r.data.steps : [])); })
      .catch((err) => { if (live.current) { setRows([]); setProblem(err.response?.data?.error || 'Claude could not be reached. Try again in a moment.'); } });
  }, [api, item.id]);
  useEffect(() => { ask(); }, [ask]);

  const kept = (rows || []).filter((r) => r.on && r.title.trim());
  const add = async () => {
    if (!kept.length || busy) return;
    setBusy(true);
    setProblem(null);
    const ok = await onAdd(kept.map((r) => ({ title: r.title.trim(), due_date: r.due_date || null, duration_minutes: r.duration_minutes || null })));
    if (!live.current) return;
    setBusy(false);
    if (ok === false) setProblem('Not all of them went in - the ones that did are under the to-do.');
  };

  return (
    <Dialog label={`Break “${item.title}” into steps`} onClose={onClose} className="tva-dialog">
      <div className="tva-dialog-body" data-assist="steps">
        <p className="tva-lede"><AiBadge /> Steps Claude suggests for this to-do. Untick, reword, re-date or reorder them, then add the ones you want.</p>
        {item.due_date && <p className="tds-quiet">None is due after the to-do itself ({longDay(String(item.due_date).slice(0, 10))}).</p>}
        {problem && <p className="tds-error" role="alert">{problem}</p>}
        {rows === null ? <Thinking words="Claude is breaking it down…" />
          : rows.length > 0 && <ProposalList rows={rows} setRows={setRows} withPlan plan={plan} label="Suggested steps" />}
        <p className="tva-privacy">Sent to Anthropic’s Claude: this to-do’s title, notes, checklist, list and client. Nothing else.</p>
      </div>
      <div className="tds-dialog-foot">
        {rows !== null && <button type="button" className="tds-link tva-again" onClick={ask} disabled={busy}>Try again</button>}
        <button type="button" className="btn btn-outline tds-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary tds-btn" disabled={busy || !kept.length} onClick={add}>
          {kept.length === 1 ? 'Add 1 sub-task' : `Add ${kept.length} sub-tasks`}
        </button>
      </div>
    </Dialog>
  );
}

/**
 * CHECKLIST FROM TEXT. Paste, then "Find the action items", then choose
 * checklist lines or sub-tasks. `canSub` false (a note, a sub-task of a
 * sub-task) and only checklist lines are offered; `canLines` false (before
 * 060) and only sub-tasks.
 */
export function ChecklistDialog({ item, api, canSub = true, canLines = true, onAddLines, onAddSubtasks, onClose }) {
  const [text, setText] = useState('');
  const [rows, setRows] = useState(null);       // null: still pasting
  const [asking, setAsking] = useState(false);
  const [as, setAs] = useState(canLines ? 'lines' : 'subtasks');
  const [problem, setProblem] = useState(null);
  const [busy, setBusy] = useState(false);
  const live = useRef(true);
  useEffect(() => () => { live.current = false; }, []);

  const find = () => {
    const t = text.trim();
    if (!t || asking) return;
    setAsking(true);
    setProblem(null);
    api.post(`/${item.id}/assist/checklist`, { text: t })
      .then((r) => { if (live.current) setRows(keyed(Array.isArray(r.data?.items) ? r.data.items : [])); })
      .catch((err) => { if (live.current) setProblem(err.response?.data?.error || 'Claude could not be reached. Try again in a moment.'); })
      .finally(() => { if (live.current) setAsking(false); });
  };

  const kept = (rows || []).filter((r) => r.on && r.title.trim());
  const add = async () => {
    if (!kept.length || busy) return;
    setBusy(true);
    setProblem(null);
    const lines = kept.map((r) => ({ title: r.title.trim(), notes: r.notes || null }));
    const ok = await (as === 'lines' ? onAddLines(lines) : onAddSubtasks(lines));
    if (!live.current) return;
    setBusy(false);
    if (ok === false) setProblem('Not all of them went in - the ones that did are on the to-do.');
  };
  const n = kept.length;
  const over = text.length > TEXT_MAX;

  return (
    <Dialog label={`Checklist from text for “${item.title}”`} onClose={onClose} className="tva-dialog">
      <div className="tva-dialog-body" data-assist="checklist">
        {rows === null ? (
          <>
            <label className="tds-field">
              <span>Paste an email or a document</span>
              <textarea className="tva-paste" rows={8} value={text} aria-describedby="tva-paste-count tva-paste-privacy"
                        placeholder="Paste the text here - Claude will list what needs doing"
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); find(); } }} />
            </label>
            <p id="tva-paste-count" className={`tva-count${over ? ' is-over' : ''}`}>
              {text.length.toLocaleString('en-GB')} / {TEXT_MAX.toLocaleString('en-GB')} characters
            </p>
            <p id="tva-paste-privacy" className="tva-privacy">
              The text is sent to Anthropic’s Claude under the firm’s account. Don’t paste client data the firm’s policy keeps out of AI tools.
            </p>
            {problem && <p className="tds-error" role="alert">{problem}</p>}
            {asking && <Thinking words="Claude is reading it…" />}
          </>
        ) : (
          <>
            <p className="tva-lede"><AiBadge /> Action items Claude found. Untick, reword or reorder them, then add them.</p>
            {problem && <p className="tds-error" role="alert">{problem}</p>}
            <ProposalList rows={rows} setRows={setRows} label="Action items" />
            {canSub && canLines && (
              <fieldset className="tva-as">
                <legend>Add them as</legend>
                <label><input type="radio" name="tva-as" checked={as === 'lines'} onChange={() => setAs('lines')} /> Checklist lines</label>
                <label><input type="radio" name="tva-as" checked={as === 'subtasks'} onChange={() => setAs('subtasks')} /> Sub-tasks</label>
              </fieldset>
            )}
          </>
        )}
      </div>
      <div className="tds-dialog-foot">
        {rows !== null && <button type="button" className="tds-link tva-again" onClick={() => { setRows(null); setProblem(null); }} disabled={busy}>Back to the text</button>}
        <button type="button" className="btn btn-outline tds-btn" onClick={onClose}>Cancel</button>
        {rows === null ? (
          <button type="button" className="btn btn-primary tds-btn" disabled={asking || !text.trim() || over} onClick={find}>
            <Icon name="sparkle" size={14} />Find the action items
          </button>
        ) : (
          <button type="button" className="btn btn-primary tds-btn" disabled={busy || !n} onClick={add}>
            {as === 'lines' ? (n === 1 ? 'Add 1 line' : `Add ${n} lines`) : (n === 1 ? 'Add 1 sub-task' : `Add ${n} sub-tasks`)}
          </button>
        )}
      </div>
    </Dialog>
  );
}

/** The detail panel's row: "AI help" and its two buttons. */
export function AssistRow({ onSteps, onChecklist, busy = false }) {
  return (
    <div className="tds-assign tva-row" data-assist-row="">
      <span className="tds-assign-label">AI help</span>
      <span className="tva-row-buttons">
        {onSteps && (
          <button type="button" className="tva-help" disabled={busy} onClick={onSteps}>
            <Icon name="sparkle" size={14} />Break into steps
          </button>
        )}
        {onChecklist && (
          <button type="button" className="tva-help" disabled={busy} onClick={onChecklist}>
            <Icon name="steps" size={14} />Checklist from text
          </button>
        )}
      </span>
    </div>
  );
}
